import { useCallback, useContext, useEffect, useState } from "react";
import Joyride from "react-joyride";
import { getTutorialState } from "../../services/tutorialService";
import { updateTutorialState } from "../../services/tutorialService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { AuthContext } from "../../contexts";
import { GetTutorialStateResponse, TutorialFeature } from "../../proto/api_pb";
import { TutorialProgressState, TutorialState } from "../../proto/api_pb";
import Mixpanel from "../../utils/mixpanel";
import { RpcError } from "grpc-web";
import { joyrideStyles } from "./styles";
import { getTrackTutorialStepsForDesktopScreen } from "./TrackTutorialSteps";
import { getTrackTutorialStepsForSmallScreen } from "./TrackTutorialSteps";
import { TrackObj } from "../../pages/MyTracks/types";

interface TrackTutorialProps {
  tracks: TrackObj[];
}

export const TrackTutorial: React.FC<TrackTutorialProps> = ({
  tracks,
}): JSX.Element => {
  const [steps, setSteps] = useState<any>();
  const [tutorialState, setTutorialState] = useState<TutorialState | undefined>(
    undefined
  );

  const [run, setRun] = useState(false);
  const { userId } = useContext(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const handleJoyrideCallback = async (data: any) => {
    const accessToken = await getValidAccessToken();
    const { status, index } = data;
    Mixpanel.track(`track_tutorial_${index}_${status}`);
    if (status === "running" && tutorialState && index === 0) {
      await updateTutorialState(
        accessToken,
        userId,
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_STARTED,
        TutorialFeature.TUTORIAL_FEATURE_TRACK_ROW
      );
    } else if (status === "running" && tutorialState && index === 1) {
      await updateTutorialState(
        accessToken,
        userId,
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_PARTIAL,
        TutorialFeature.TUTORIAL_FEATURE_TRACK_ROW
      );
    } else if (status === "finished" && tutorialState && index) {
      await updateTutorialState(
        accessToken,
        userId,
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_COMPLETE,
        TutorialFeature.TUTORIAL_FEATURE_TRACK_ROW
      );
    }
  };

  const fetchAndSetTutorialState = useCallback(async () => {
    if (userId && userId.length > 0) {
      const accessToken = await getValidAccessToken();
      const response = await getTutorialState(
        accessToken,
        userId,
        TutorialFeature.TUTORIAL_FEATURE_TRACK_ROW
      );
      if (response instanceof GetTutorialStateResponse) {
        setTutorialState(response.getTutorialState());
      } else if (response instanceof RpcError) {
        if (response.code === 5) {
          setTutorialState(new TutorialState());
        }
      }
    }
  }, [getValidAccessToken, userId]);

  useEffect(() => {
    fetchAndSetTutorialState();
  }, [fetchAndSetTutorialState]);

  useEffect(() => {
    if (!tutorialState) {
      return;
    }
    if (
      tutorialState.getTutorialProgressState() !==
        TutorialProgressState.TUTORIAL_PROGRESS_STATE_COMPLETE &&
      tracks.length > 0
    ) {
      setRun(true);
      if (window.innerWidth > 700) {
        setSteps(getTrackTutorialStepsForDesktopScreen());
      } else {
        setSteps(getTrackTutorialStepsForSmallScreen());
      }
    }
  }, [tracks.length, tutorialState]);

  return (
    <>
      <Joyride
        continuous
        callback={handleJoyrideCallback}
        run={run}
        steps={steps}
        hideCloseButton
        scrollToFirstStep
        showProgress
        disableScrolling
        styles={joyrideStyles}
      />
    </>
  );
};
